"use client"
import { EMAIL_ADDRESS, LINKEDIN_URL, TEAMS_LINK, TeamsLogo, WHATSAPP_NUMBER, WhatsAppLogo } from "@/lib";
import { Linkedin, Mail, ArrowUp, Zap } from "lucide-react";
import { motion } from "framer-motion";

export default function Footer() {
    
    // Define quick links for navigation consistency
    const footerLinks = [
        { name: 'Analyzer', href: '#analyzer' },
        { name: 'Services', href: '#services' },
        { name: 'Plans', href: '#plans' },
        { name: 'Contact', href: '#contact' },
    ];

    const scrollToTop = (e: React.MouseEvent) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <footer className="bg-slate-950 border-t border-slate-800 relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                
                {/* Top Section: Logo, Navigation, and Scroll-to-Top Button */}
                <div className="flex flex-col md:flex-row justify-between items-center md:items-start border-b border-slate-800 pb-10 mb-8 space-y-8 md:space-y-0">
                    
                    {/* Brand/Logo */}
                    <div className="text-center md:text-left">
                        <a href="#hero" className="text-3xl font-extrabold text-white tracking-tighter">
                            Mohan <span className="text-indigo-400">WorkSpace</span>
                        </a>
                        <p className="mt-2 text-sm text-slate-500 max-w-xs">
                            Expert Tech Leadership & Strategic Advisory. Engineered for scalability.
                        </p>
                    </div>

                    {/* Quick Links Navigation */}
                    <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
                        <h3 className="w-full text-center md:text-left text-sm font-semibold uppercase text-slate-500 mb-2">Quick Links</h3>
                        {footerLinks.map((link) => (
                            <a 
                                key={link.name} 
                                href={link.href} 
                                className="text-base text-slate-400 hover:text-indigo-400 transition-colors"
                            >
                                {link.name}
                            </a>
                        ))}
                    </div>

                    {/* Scroll to Top Button */}
                    <motion.button 
                        onClick={scrollToTop}
                        className="p-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/40 transition-all"
                        aria-label="Scroll back to top"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <ArrowUp className="w-5 h-5" />
                    </motion.button>
                </div>

                {/* Bottom Section: Copyright and Socials */}
                <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                    
                    {/* Copyright */}
                    <p className="text-sm text-slate-600">
                        &copy; 2025 Mohan WorkSpace. All rights reserved.
                    </p>
                    
                    {/* Social/Contact Icons */}
                    <div className="flex space-x-6">
                        {/* LinkedIn */}
                        <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="LinkedIn">
                            <Linkedin className="w-6 h-6" />
                        </a>
                        
                        {/* Email */}
                        <a href={`mailto:${EMAIL_ADDRESS}`} className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Email">
                            <Mail className="w-6 h-6" />
                        </a>

                        {/* WhatsApp (Adding based on project context) */}
                        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="WhatsApp">
                            <WhatsAppLogo className="w-6 h-6" />
                        </a>
                        
                        {/* Teams (Adding based on project context) */}
                        <a href={TEAMS_LINK} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Teams Community">
                            <TeamsLogo className="w-6 h-6" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    )
}